// src/hooks/usePlatformClaims.js
//
// Thin wrapper that reads the signed-in user's JWT custom claims from the
// AuthContext and exposes the canonical platform-level predicates from
// src/constants/roles.js. Use this anywhere the UI needs to branch on
// "are they a Scrimly founder / support agent / finance reader?" — e.g.
// the AdminRoute guard, impersonation-banner visibility, the grant-claims
// admin page.
//
// Why a hook over direct `useAuth().claims` access:
//   • centralizes the claim-name → predicate mapping in one place, so
//     renaming or adding a claim doesn't require touching every caller
//   • mirrors the server-side API in functions/core/roles.js (isFounder,
//     isSupport, isFinance, isPlatform) so grep-ability is symmetric
//   • returns an empty-claims fallback when auth is still loading, so
//     callers don't have to null-check (predicates just return false)
//
// Custom claims live on the JWT and are set server-side only (see
// scripts/platformClaims.mjs). They can't be self-promoted from the
// client, unlike the legacy users/{uid}.role field.

import { useAuth } from '@/context/AuthContext';
import {
  isFounder as _isFounder,
  isSupport as _isSupport,
  isFinance as _isFinance,
  isPlatform as _isPlatform,
} from '@/constants/roles';

export function usePlatformClaims() {
  const { claims } = useAuth();
  const safe = claims || {};
  return {
    claims:     safe,
    isFounder:  _isFounder(safe),
    isSupport:  _isSupport(safe),
    isFinance:  _isFinance(safe),
    isPlatform: _isPlatform(safe),
  };
}

export default usePlatformClaims;
